import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  SCIENTIFIC_MODEL,
  getAvailableStructurePredictionModels,
  getModelDisplayName,
  getModelExternalLink,
  isPreviewModel,
  modelEgressesData,
} from 'model-hub/scientific-models/ScientificModelConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (model: string) => void;
  disabled?: boolean;
  className?: string;
}

function PreviewBadge(): JSX.Element {
  return <Tag size={TagSize.SMALL} color={TagColor.PURPLE} text="PREVIEW" />;
}

export default function ModelSelector({
  selectedModel,
  onModelChange,
  disabled = false,
  className,
}: ModelSelectorProps): JSX.Element {
  // Get available models based on feature flags
  const configEnabledModels = getAvailableStructurePredictionModels();

  return (
    <div className={className}>
      <div className={css(styles.label)}>Model</div>
      <SelectDropdown
        displayName={
          <div className={css(styles.rowContent)}>
            <span>{getModelDisplayName(selectedModel)}</span>
          </div>
        }
        disabled={disabled}
        menu={
          <Menu width={MenuWidth.FULL} value={selectedModel}>
            {configEnabledModels.map((model) => (
              <Option
                key={model}
                value={model}
                onSelect={() => {
                  onModelChange(model);
                }}
              >
                <div className={css(styles.rowContent)}>
                  <span>{getModelDisplayName(model)}</span>
                  {isPreviewModel(model) && <PreviewBadge />}
                </div>
              </Option>
            ))}
          </Menu>
        }
      />
      <div className={css(styles.helpText)}>
        <span>
          MSAs (multi-sequence alignments) increase accuracy by providing evolutionary context to the
          structure prediction model, but will add time to the prediction.{' '}
          {getModelDisplayName(SCIENTIFIC_MODEL.ALPHAFOLD)} uses an MSA. Support for MSAs for all other models
          is coming soon.
        </span>
        {!modelEgressesData(selectedModel) && (
          <>
            <br />
            <br />
            <span>
              {getModelDisplayName(selectedModel)} is run on Benchling's infrastructure. Your data is not sent
              to third parties.
            </span>
          </>
        )}
      </div>
      <div className={css(styles.linkContainer)}>
        <a
          href={getModelExternalLink(selectedModel)}
          target="_blank"
          rel="noopener noreferrer"
          className={css(styles.modelLink)}
        >
          Learn more about {getModelDisplayName(selectedModel)}
        </a>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  label: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    marginBottom: '6px',
    color: GRAY_DARKER,
  },
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
    maxWidth: '600px',
  },
  linkContainer: {
    marginTop: '8px',
  },
  modelLink: {
    ...FONT_PROXIMA_13,
    color: '#0066cc',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  previewBadge: {
    marginLeft: '8px',
  },
});
